import { globalConfirm } from '../components/ConfirmDialog';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import AppShell from '../components/layout/AppShell';
import { PageHeader, DataTableToolbar, PaginationBar, ActiveBadge, EmptyState, LoadingSpinner } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import InteractiveTable from '../components/InteractiveTable';
import api, { getApiErrorMessage } from '../services/api';
import ModuleReports from './Reports/ModuleReports';
import { DOCUMENT_TYPES_REPORTS } from './Reports/reportConfigs';
import { BarChart2, FileText as FileTextIcon } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/usePermissions';
import { Plus, Download, Upload, X, Image } from 'lucide-react';
import DataFilesAnalytics from './DataFilesAnalytics/DataFilesAnalytics';
import { documentTypesConfig } from './DataFilesAnalytics/configs';
import { LayoutDashboard as AnalyticsIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { code: '', description: '', budget: 0, company_name: '', company_address: '', active: true };

export default function DocumentTypesPage() {
  const [records, setRecords] = useState([]);
  const { canWrite: _canWrite } = usePermissions();
  const canWriteData = _canWrite('document_type');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [viewTab, setViewTab] = useState('analytics');
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortState, setSortState] = useState({ field: 'code', dir: 'asc' });
  const handleSort = useCallback((field) => {
    setSortState(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/data/document-types', { params: { search, page, page_size: pageSize, sort_by: sortState.field, sort_dir: sortState.dir } });
      setRecords(res.data.data || []); setTotal(res.data.total);
    } catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load'); }
    setLoading(false);
  }, [search, page, pageSize, sortState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  const f = (field, val) => setForm(p => ({ ...p, [field]: val }));

  const openCreate = () => { setForm(EMPTY); setIsEdit(false); setLogoFile(null); setLogoPreview(null); setModalOpen(true); };
  const openEdit = r => {
    setForm({ ...r }); setIsEdit(true);
    setLogoPreview(r.logo_path ? `${BACKEND_URL}/api/data/document-types/${r.code}/logo` : null);
    setLogoFile(null); setModalOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) await api.put(`/api/data/document-types/${form.code}`, form);
      else await api.post('/api/data/document-types', form);
      if (logoFile && form.code) {
        const fd = new FormData(); fd.append('file', logoFile);
        await api.post(`/api/data/document-types/${form.code}/logo`, fd);
      }
      toast.success(isEdit ? 'Updated' : 'Created');
      setModalOpen(false); load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const deleteRecord = async () => {
    if (!await globalConfirm('Delete this document type?')) return;
    try { await api.delete(`/api/data/document-types/${form.code}`); toast.success('Deleted'); setModalOpen(false); load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Delete failed')); }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = ev => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const toggleFav = async (r) => {
    const { toggleFavorite } = await import('../utils/favorites');
    const result = await toggleFavorite('document-types', r.code);
    if (result !== null) {
      setRecords(prev => {
        const updated = prev.map(rec => (rec === r) ? { ...rec, is_favorite: result } : rec);
        const starred = updated.filter(rec => rec.is_favorite);
        const unstarred = updated.filter(rec => !rec.is_favorite);
        return [...starred, ...unstarred];
      });
    }
  };

  const dtDefaultCols = useMemo(() => [
    { key: 'code', label: 'Document Type', width: 150, sortField: 'code', visible: true },
    { key: 'description', label: 'Description', width: 220, sortField: 'description', visible: true },
    { key: 'company_name', label: 'Company Name', width: 180, sortField: 'company_name', visible: true },
    { key: 'budget', label: 'Budget', width: 130, sortField: 'budget', align: 'right', visible: true },
    { key: 'active', label: 'Active', width: 80, sortField: 'active', visible: true },
  ], []);

  const renderDtCell = useCallback((col, r) => {
    switch (col.key) {
      case 'code': return <span className="font-mono font-semibold text-zinc-900 text-sm">{r.code}</span>;
      case 'description': return <span className="text-sm">{r.description}</span>;
      case 'company_name': return <span className="text-sm">{r.company_name}</span>;
      case 'budget': return <span className="text-sm font-mono tabular-nums">{(r.budget || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>;
      case 'active': return <ActiveBadge active={r.active} />;
      default: return <span className="text-sm">{r[col.key] != null ? String(r[col.key]) : '—'}</span>;
    }
  }, []);

  return (
    <AppShell>
      <PageHeader title="Document Type" description={`Total records: ${total}`} helpGuide="data-files"
        actions={viewTab === 'list' ? <>
          <Button size="sm" variant="outline"><Download className="h-4 w-4 mr-1" /> Export</Button>
          <Button size="sm" onClick={openCreate} style={canWriteData ? {} : {display:"none"}} data-testid="doc-type-add-button"><Plus className="h-4 w-4 mr-1" /> Add New</Button>
        </> : null}
      />
      <div className="flex gap-4 border-b mb-4" data-testid="document-types-view-tabs">
        <button onClick={() => setViewTab('analytics')} data-testid="document-types-tab-analytics"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'analytics' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <AnalyticsIcon className="h-4 w-4 inline mr-1.5" /> Analytics
        </button>
        <button onClick={() => setViewTab('list')} data-testid="document-types-tab-list"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'list' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <FileTextIcon className="h-4 w-4 inline mr-1.5" /> Document Types
        </button>
        <button onClick={() => setViewTab('reports')} data-testid="document-types-tab-reports"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'reports' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <BarChart2 className="h-4 w-4 inline mr-1.5" /> Reports
        </button>
      </div>
      {viewTab === 'analytics' ? (<DataFilesAnalytics {...documentTypesConfig} onCardClick={() => setViewTab('list')} />) : viewTab === 'reports' ? (
        <ModuleReports reports={DOCUMENT_TYPES_REPORTS} apiPrefix="/api/module-reports/data" module="Document Types" />
      ) : (<>
      <DataTableToolbar searchValue={search} onSearch={v => { setSearch(v); setPage(1); }} onClear={() => { setSearch(''); setPage(1); }} searchPlaceholder="Search document types..." toolbarSlotId="doctype-listing-toolbar" />
      <div className="rounded-xl border bg-card">
        {loading ? <div className="py-12"><LoadingSpinner /></div> : records.length === 0 ? <EmptyState title="No document types" action={<Button size="sm" onClick={openCreate} style={canWriteData ? {} : {display:"none"}}>Add</Button>} /> : (
          <InteractiveTable
            tableId="doctype-listing"
            toolbarPortalId="doctype-listing-toolbar"
            defaultCols={dtDefaultCols}
            data={records}
            renderCell={renderDtCell}
            onRowClick={(row) => openEdit(row)}
            sortState={sortState}
            onSort={handleSort}
            rowKey="code"
          />
        )}
        <div className="px-4"><PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} /></div>
      </div>
      </>)}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Document Type - {isEdit ? 'Edit' : 'Add'}
              <div className="flex items-center gap-2 mr-6"><span className="text-sm font-normal text-muted-foreground">Active</span><Switch checked={form.active} onCheckedChange={v => f('active', v)} /></div>
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6 py-2">
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Document Type *</Label><Input value={form.code} onChange={e => f('code', e.target.value)} placeholder="e.g. PO" disabled={isEdit} /></div>
              <div className="space-y-1.5"><Label>Description *</Label><Input value={form.description} onChange={e => f('description', e.target.value)} placeholder="Purchase Order" /></div>
              <div className="space-y-1.5"><Label>Budget</Label><Input type="number" value={form.budget} onChange={e => f('budget', parseFloat(e.target.value) || 0)} /></div>
              <div className="space-y-1.5"><Label>Company Name</Label><Input value={form.company_name} onChange={e => f('company_name', e.target.value)} placeholder="Company name for this document" /></div>
              <div className="space-y-1.5">
                <Label>Company Address</Label>
                <Textarea value={form.company_address} onChange={e => f('company_address', e.target.value)} rows={3} maxLength={255} placeholder="Full company address" />
                <p className="text-xs text-muted-foreground text-right">Characters remaining: {255 - (form.company_address?.length || 0)}</p>
              </div>
            </div>
            <div className="space-y-3">
              <Label>Company Logo</Label>
              <div
                className="border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary transition-colors min-h-[120px]"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const f2 = e.dataTransfer.files?.[0]; if (f2) { setLogoFile(f2); const r = new FileReader(); r.onload = ev => setLogoPreview(ev.target.result); r.readAsDataURL(f2); } }}
                role="button" aria-label="Upload company logo"
              >
                {logoPreview ? (
                  <div className="relative">
                    <img src={logoPreview} alt="Logo preview" className="max-h-24 max-w-full object-contain" />
                    {logoFile && <p className="text-xs text-muted-foreground mt-1">Size: {(logoFile.size / 1024).toFixed(1)}Kb</p>}
                  </div>
                ) : (
                  <><Image className="h-8 w-8 text-muted-foreground" /><p className="text-sm text-muted-foreground">Drag & drop or</p></>
                )}
                <Button type="button" size="sm" variant="outline" onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}>Select File</Button>
                {logoPreview && <Button type="button" size="sm" variant="ghost" onClick={e => { e.stopPropagation(); setLogoFile(null); setLogoPreview(null); }} className="text-destructive"><X className="h-3 w-3 mr-1" />Remove</Button>}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoSelect} aria-label="Select logo file" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            {isEdit && <Button variant="destructive" onClick={deleteRecord}>Delete</Button>}
            <Button onClick={save} disabled={!canWriteData} disabled={saving || !form.code || !form.description}>{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
